import { FormikProvider, useFormik, Form } from "formik";
import { z } from "zod";
import React from "react";
import {
    RuleGroupType,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

export interface RuleGroupSchema {
    name: string;
}

const fields = createFormikFields<RuleGroupSchema>();

interface RuleGroupFormProps {
    ruleGroup?: TransactionRuleGroup;
    onHide: () => void;
    isPending: boolean;
    onSubmitRuleGroup: (data: RuleGroupSchema) => Promise<unknown> | undefined;
    type: RuleGroupType;
}

const ruleGroupFormSchema = z.object({
    name: z.string().min(1, "Name is required"),
});

export const RuleGroupForm: React.FC<RuleGroupFormProps> = ({
    ruleGroup,
    onHide,
    isPending,
    onSubmitRuleGroup,
    type,
}) => {
    const formik = useFormik<RuleGroupSchema>({
        initialValues: {
            name: ruleGroup?.name ?? "",
        },
        validate: validateFormikWithZodSchema(ruleGroupFormSchema),
        onSubmit: submitHelper({
            loading: isPending,
            handler: async (data) => {
                await onSubmitRuleGroup({ name: data.name });
                onHide();
            },
        }),
    });

    return (
        <>
            <StandardModalHeader>
                {ruleGroup ? "Update" : "Create"}{" "}
                {type === RuleGroupType.ACCOUNTING
                    ? "Accounting"
                    : "Transaction"}{" "}
                Rule Group
            </StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <fields.Text name="name" label="Rule Group" size="xl" />

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onHide}
                                data-testid="rule-group-modal-cancel-button"
                                disabled={isPending}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                type="submit"
                                loading={isPending}
                                data-testid="rule-group-modal-save-button"
                                disabled={isPending}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Save rule group",
                                    enableOnFormTags: true,
                                }}
                            >
                                {ruleGroup ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </>
    );
};
